import React, { useEffect, useRef } from 'react';
import _throttle from 'lodash/throttle';
import useNoRenderRef from '../../../../../hooks/useNoRenderRef';

type TProps = {
  currentZoom: number;
  onAcceptClick: () => void;
  onDiscardClick: () => void;
  onZoomChange: (newZoom: number, isDone: boolean) => void;
  onZoomDragStart: () => void;
};

const MAX_ZOOM = 5;

function ZoomPanel({
  currentZoom,
  onAcceptClick,
  onDiscardClick,
  onZoomChange,
  onZoomDragStart
}: TProps) {
  const sliderElRef = useRef(null);
  const sliderRef = useRef(null);
  const loadedZoomRef = useRef(Math.max(MAX_ZOOM, currentZoom));

  const onZoomNoRenderRef = useNoRenderRef(onZoomChange);

  useEffect(() => {
    return () => sliderRef?.current?.noUiSlider?.destroy();
  }, []);

  useEffect(() => {
    if (sliderRef.current) return;

    sliderRef.current = window.noUiSlider.create(sliderElRef.current, {
      start: currentZoom,
      step: 0.01,
      range: {
        min: 1,
        max: loadedZoomRef.current
      }
    });

    let initialized = false;
    const slider = sliderRef.current;

    slider.on('start', onZoomDragStart);
    slider.on('update', function (valueArray) {
      if (!initialized) {
        // The 'update' event fires when the slider is created, but we don't
        // want that. This early return skips that execution.
        initialized = true;
        return;
      }

      const newZoomLevel = +valueArray[0];

      onZoomNoRenderRef.current(newZoomLevel);
    });

    slider.on('set', function (valueArray) {
      const newZoomLevel = +valueArray[0];

      onZoomNoRenderRef.current(newZoomLevel, true);
    });
  }, [currentZoom, onZoomNoRenderRef, onZoomDragStart]);

  return (
    <div className="wysiwyg-editor-image-control-panel-zoom">
      <div
        className="wysiwyg-editor-image-control-panel-zoom-slider"
        ref={sliderElRef}
      />
      <div
        className="wysiwyg-editor-image-control-panel-zoom-buttons"
        style={{
          display: 'flex',
          justifyContent: 'space-evenly',
          marginTop: 6
        }}
      >
        <button
          type="button"
          className="wysiwyg-editor-image-control-panel-zoom-button-accept"
          style={{
            alignItems: 'center',
            background: 'none',
            display: 'flex',
            fontSize: 0,
            height: 30,
            justifyContent: 'center',
            width: 30
          }}
          onClick={onAcceptClick}
        >
          Save zoom changes
          <svg
            width="12"
            height="9"
            viewBox="0 0 12 9"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11 1L3.5 8L1 5.5"
              stroke="#228F67"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          className="wysiwyg-editor-image-control-panel-zoom-button-cancel"
          style={{
            alignItems: 'center',
            background: 'none',
            display: 'flex',
            fontSize: '0px',
            height: 30,
            justifyContent: 'center',
            width: 30
          }}
          onClick={onDiscardClick}
        >
          Discard changes
          <svg
            width="8"
            height="8"
            viewBox="0 0 8 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.5 0.5L0.5 7"
              stroke="#CC3340"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M0.5 0.5L7.5 7"
              stroke="#CC3340"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default ZoomPanel;
